import { useEnvironmentInterface } from "environment-interface";
import { env } from "environment-interface/env";
import { useEditorFeature, useSandboxEditor } from "features/editor/Feature";
import type { SandboxEditor } from "features/types/editor";
import { Dropdown, Button, Stack, Tooltip } from "prism-react";
import { renameSandbox } from "queries/sandbox/api";
import * as React from "react";

import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { paths } from "utils/router";
import { executeVSCodeCommand } from "utils/vscode";

import { AnimatedDiv } from "../AnimatedDiv";
import type { EditorTypeProps } from "../EditorType";
import { RenamableTitle } from "../RenamableTitle";
import { TitleMenu } from "../TitleMenu";
import { TitleUI } from "../TitleUI";

import { SandboxPopover } from "./SandboxPopover";

export const SandboxTitleUI: React.FC<{
  sandbox: SandboxEditor;
  onRename: (value: string) => Promise<unknown>;
}> = ({ sandbox, onRename }) => {
  const [showPopover, setShowPopover] = React.useState(false);
  const handleClick = () => setShowPopover(true);
  const editor = useSandboxEditor();
  const activeWorkspace = useActiveWorkspace();
  const canRename = editor.allowedCapabilities.renameProject;

  const title = editor.type === "sandbox" ? editor.title : "";
  const type = editor.isCloud ? "devbox" : "sandbox";

  const editorType: EditorTypeProps = sandbox.customTemplate
    ? {
        type,
        iconUrl: sandbox.customTemplate.iconUrl,
        templateName: sandbox.template,
        git: sandbox.git ? sandbox.git : undefined,
        workspaceId: sandbox.workspace?.id,
      }
    : { type, workspaceId: sandbox.workspace?.id };

  const sandboxWorkspaceName = editor.workspace?.name;

  React.useEffect(() => {
    document.title = `${title} - CodeSandbox`;
  }, [title]);

  const sandboxFromActiveWorkspace =
    editor.workspace?.id === activeWorkspace?.id;

  let collection = "Drafts";
  if (editor.collection?.path) {
    if (editor.collection.path === "/") {
      collection = "All folders";
    } else {
      collection = editor.collection.path.slice(
        1,
        editor.collection.path.length,
      ); // remove slash
    }
  }

  return (
    <TitleUI type={editorType}>
      <Stack gap={0} horizontal>
        {sandboxWorkspaceName ? (
          <>
            <AnimatedDiv>
              <Tooltip content="Open workspace dashboard">
                <Button
                  as="a"
                  css={{ padding: "$1" }}
                  href={paths.dashboard.recent(editor.workspace?.id)}
                >
                  {sandboxWorkspaceName}
                </Button>
              </Tooltip>
            </AnimatedDiv>
            <AnimatedDiv>/</AnimatedDiv>
          </>
        ) : null}

        {(editor.draft || sandboxFromActiveWorkspace) &&
          !editor.customTemplate && (
            <>
              <AnimatedDiv>
                <Tooltip
                  content={editor.collection ? "Open folder" : "Open Drafts"}
                >
                  <Button
                    as="a"
                    css={{ padding: "$1" }}
                    href={
                      editor.collection
                        ? paths.dashboard.collection(
                            editor.collection.path,
                            editor.workspace?.id,
                          )
                        : paths.dashboard.drafts(editor.workspace?.id)
                    }
                  >
                    {collection}
                  </Button>
                </Tooltip>
              </AnimatedDiv>

              <AnimatedDiv>/</AnimatedDiv>
            </>
          )}

        <SandboxPopover
          sandbox={sandbox}
          setShowPopover={setShowPopover}
          showPopover={showPopover}
        >
          <RenamableTitle
            canRename={canRename}
            displayValue={title}
            onClick={handleClick}
            onRename={onRename}
            projectType={editor.type}
            value={title}
          />
        </SandboxPopover>
      </Stack>

      <Menu canRename={canRename} sandbox={sandbox} />
    </TitleUI>
  );
};

export function SandboxTitle() {
  const project = useSandboxEditor();

  return <SandboxWithTitle sandbox={project} />;
}

export function SandboxWithTitle({ sandbox }: { sandbox: SandboxEditor }) {
  const [, dispatchProject] = useEditorFeature();

  /**
   * @todo handle mutation error
   */
  const handleRename = async (newTitle: string) => {
    const data = await renameSandbox(sandbox.id, newTitle);
    dispatchProject({
      type: "UPDATE_SANDBOX",
      project: {
        title: newTitle,
        // WE always get alias back
        alias: data.renameSandbox.alias!,
      },
    });
  };

  return <SandboxTitleUI onRename={handleRename} sandbox={sandbox!} />;
}

const Menu: React.FC<{
  sandbox: SandboxEditor;
  canRename: boolean;
}> = ({ sandbox, canRename }) => {
  const { clipboard } = useEnvironmentInterface();

  return (
    <TitleMenu canRename={canRename} isInteractive>
      {canRename ? (
        <Dropdown.Item
          onSelect={(e) => {
            e.stopPropagation();

            executeVSCodeCommand("codesandbox.openCsbTab", {
              type: "SANDBOX_INFO",
              isCloud: sandbox.isCloud,
            });
          }}
        >
          Edit info...
        </Dropdown.Item>
      ) : null}

      <Dropdown.Item
        onSelect={(e) => {
          e.stopPropagation();
          clipboard.write(
            `${env.PUBLIC_UI_URL}${env.PUBLIC_BASE_PATH || ""}/sandbox/${
              sandbox.alias
            }`,
          );
        }}
      >
        Copy link
      </Dropdown.Item>
    </TitleMenu>
  );
};
